/**
 * Setup Wizard Component
 *
 * Unified single-page UI for the requirements-based planning workflow.
 * Handles document upload, decomposition, research, planning, and execution.
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import type React from 'react';
import { WorkflowProgress, type WorkflowStep } from './WorkflowProgress';
import { RequirementsList, type RequirementListItem } from './RequirementsList';
import { ActivityFeed, type ActivityEvent } from './ActivityFeed';
import { API_BASE, WS_BASE } from '@/lib/config';

// Sample requirements document
const ZOOMLLY_JAMS_SAMPLE = `# Company Overview

Zoomlly Jams is a premium music subscription service offering access to curated playlists, artist interviews, and exclusive live-streamed performances. Customers reach out for help with subscription management, event ticket purchases, and streaming support. The contact center must deliver personalized, multilingual service and prioritize Premium subscribers using advanced Zoom CX tools.

Zoomlly Jams is head quartered in Nashville Tennessee. Their address is: 401 Gay St, Nashville, TN 37219

| Agent Name | Department | Role | Skills | Languages | Location |
|------------|------------|------|--------|-----------|----------|
| Kevin Hanks | Customer Experience | Agent | Event Sales, Streaming Support, Subscription Management | English: 10 | Los Angeles |
| Juanita Reed | Customer Experience | Agent | Event Sales, Subscription Management | Spanish: 10, English: 5 | Nashville |
| Melanie Sharp | Customer Experience | Agent | Streaming Support, Event Sales | English: 10, Spanish: 3 | Nashville |
| Cara Knox | Customer Experience | Supervisor | Event Sales, Streaming Support, Subscription Management | English: 10 | Nashville |
| Jose Manteo | Information Technology | Admin | | | Los Angeles |

# Contact Center Requirements

### Voice Engagements

- When a customer calls Zoomlly Jams, callers are greeted with a language selection prompt for English or Spanish(US). The voice prompts language adjusts dynamically based on the caller's choice.
- Customers can route to Subscription Management, Streaming Support, or Event Sales. All IVR menus support both touch-tone (DTMF) and speech input.
- Zoomlly Jams maintains a curated Address Book of customer contacts (see section: Assets). This Address Book should include custom fields to indicate whether contacts are "Premium" members. In addition, the address book will contain customer Account Numbers which will be used to integrate into Zoomlly Jams subscription management system
- Integration into Zoomlly Jams subscription management system:
    - Zoom Contact Center will be integrating into Zoomlly Jams subscription management system via API (see section: Assets).
    - If a customer selects *Subscription Management* from the IVR, they can hear their subscription information by providing their Account Number via DTMF. The subscription information should contain the plan type, price and renewal date played back to the customer.
    - For known callers with an Account Number in the Address Book, the system automatically sends the account number so they won't be asked to enter it i.e., "We have looked up your phone number and found account 2250."
    - If needed, customers can opt to speak to a live support agent for help with subscriptions after retrieving their Subscription information.
- Zoomlly Jams requires that all agents be assigned to a single voice queue. Contacts should be routed to agents based on a combination of skill match (Streaming Support, Event Sales, Subscription Management) and language proficiency (English, Spanish). If no agents match the specific skills within 180 seconds, the priority should be relaxed to allow less skilled agents to respond, provided they possess the necessary language proficiency for the engagement.
- Zoomlly Jams prefers that customers are redirected to the last agent they interacted with within the past hour in the event a call is dropped and a customer needs to call back.
- Customers identified as Premium members are prioritized in Queue over other callers.
- When an inbound call arrives, the reason for calling (Subscription Management, Streaming Support, or Event Sales) must be displayed on the agent's inbound call notification.
- If all agents in the queue are opted-out; calls bypass routing to the queue and go directly to voicemail with a branded greeting.
- Customers must be told their position in queue when first entering the queue.
- Customers waiting in queue longer than 2 minutes are offered a callback option or voicemail, along with queue position updates.
- Agents can upgrade any voice call to a video session for troubleshooting purposes.
- After each engagement, Zoomlly Jams wants to present a survey to their customers rating the interaction with the Agent. The survey should also support Spanish language.
- Agents must have the ability to opt-out of queues at any time but *must* provide a reason for opt-out.
- Customers must also be able to schedule a callback from the Zoomlly Jams website by pressing a "Schedule a callback" button. The button must be black and presented on the lower-left portion of their website. The furthest callback date can only be 5 days. Zoomlly Jams wants a separate queue for agents handling Scheduled Callbacks.

---
### Virtual Agent & Web Chat

- Zoomlly Jams would like a button on their website for customers to talk to a Virtual Agent. The button must be white, and say "Chat with us", presented on the lower-right portion of the website.
- Zoom Virtual Agent (ZVA) will handle all customer engagements first and must:
    - Detect customer browser language to offer chat support in English or Spanish(US).
    - Answers questions using the Zoomlly Jams Knowledge Base.
    - Supports subscription information lookups by asking the customer to enter their account number whenever prompted.
    - If a customer says they want to cancel their subscription, ZVA must collect the account number from the customer and route the call to a live agent. The account number must be displayed to the agent on an inbound notification.
    - Escalates to a live agent when ZVA is unable to resolve customer issues after 2 attempts or the customer requests to speak to a live agent.
    - Prevents users from entering sensitive data like credit card numbers or USA based social security numbers (123-45-6789).
- Chat engagements must be able to be upgraded to video for troubleshooting purposes.
- After all chat interactions, customers receive a language-specific *in-line* survey.

---
### Video Experience

- Customers can also choose to connect to a video agent for technical support from the Zoomlly Jams website. The button must be green and say "Technical Support", presented on the lower-middle portion of their website.
- When the video button is pressed, the customer is then prompted to provide a brief text description(max 32 characters) of the issue they are having. This issue must be captured and presented to the agent on the inbound engagement notification.
- A waiting lobby for video engagements must display a company branded advertisement.
- If the queue wait time exceeds 5 minutes, a video callback option is presented. If accepted the customer should receive an email link to join the callback every 60 seconds.
- Video Engagements must support Remote Control for customer troubleshooting purposes.

---
### AI Expert Assist

- Zoom AI Expert Assist must monitor all voice, video, and chat engagements to:
    - Auto-suggest answers to agents from the Zoomlly Jams Knowledge Base.
    - Trigger Subscription information lookup for the Agent when customers ask for help with their Subscription.
    - For voice engagements only: Present agents with a greeting recommendation when interacting with "Premium" plan customers. This greeting will automatically display at the beginning of each engagement, providing a ready-to-use script. The greeting includes the agent's name and dynamically adjusts based on which agent is handling the interaction. Example: "Thank you for being a Zoomlly Jams Premium member. My name is Kevin, how can I help you today?"
    - Provide agents a step-by-step guided script for when a customer says they want to cancel their subscription at any time.
    - Dispositions like "Subscription Renewal," "Subscription Cancellation," "Event Sales," and "Streaming Support" are auto-tagged by AI.
    - Agents notes should automatically be generated after every engagement.

---
### Quality Management

- Zoomlly Jams only wants inbound interactions analyzed by Quality Management.
- All inbound interactions must be auto-scored by Quality Management without the need of a Supervisor evaluation.
- Zoomlly Jams wants to flag conversations where the customer is calling about subscription cancellations.
- Zoomlly Jams wants supervisors to evaluate interactions where the conversation is about "subscription cancellations" on a weekly basis. All agents should be included however only 25% of the interactions per agent should be sampled. Supervisors have 3 days after task assignment to complete the Evaluations.
- Zoomlly Jams also wants to track whenever the name of a Competitor product is mentioned.
- Supervisors want a report of 'Evaluation Results by Agent' delivered via email on a weekly basis.

---
### Workforce Management

- Zoom WFM will handle Scheduling, forecasting, and real-time adherence for all queues and channels.
- Include all the agents and queues in a scheduling group, ensuring to accommodate all channel types.
- Agents should be able to submit PTO requests. PTO requests require supervisor approval.
- Agents should also have the ability to initiate shift swaps with other agents, these do not require supervisor approval. However, an agent must be limited to 2 shift swaps a month.
- Enable shift bidding for agents. Create a shift bid for a future date prioritizing selections according to the agent's tenure within the company.
- Successfully generate a forecast for a week in the future and apply that forecast to a schedule.

---
### Outbound Campaigns

- Zoomlly Jams wants to run promotional campaigns for upcoming promotions using Zoom's campaign dialer.
- A curated Contact List of opt-in customers is used for outbound dialing. This contact list must be sync'd with the Zoomlly Jams Customer Address Book.
- The contact center should automatically place calls to these contacts, routing answered calls to agents in the queue. For unanswered customer calls, a pre-recorded message detailing the promotional offer should be left on the customers' voicemail if detected.
- Outbound calls should only occur during 9 AM to 5 PM based on the contacts area code, and still must fall within the campaign's business hours.


# Assets

| Customer Name | Account Number | Phone Number | Premium Member | Preferred Language |
|---------------|----------------|--------------|----------------|--------------------|
| Benito Sylva | 2250 | +13522900413 | true | Spanish |
| Erica Truman | 2251 | +12312518124 | | English |
| Will Callaghan | 2252 | +12393652014 | true | English |
| Partner Test User 1 | 2253 | xxx-xxx-xxxx | | |
| Partner Test User 2 | 2254 | xxx-xxx-xxxx | true | |

---
## Zoomlly Jams Website

https://zcx.public.zoomdemos.com/jams

---
## Zoomlly Jams Knowledge Base

Create your own Zoom Doc on your lab account called "Zoomlly Jams FAQ" to be used as a Knowledge Base. The Zoom Doc must have at least 10 topics each in their own page.
Reference Doc: https://docs.zoom.us/doc/ec61w0DITJqsbqGAGsOAjw

---
## Zoomlly Jams Subscription Management System

- REST URL: https://zoomllytire-0de7.restdb.io/rest/subscriptions
- API Key: 67993f8c6ddc2631c5dcb9be

---
## AI Expert Assist Endpoint URL

Used for Information Lookup tool: https://api.zaaplab.com/subscriptions

---
## WFM Data Generator Tool

You may use this app to generate WFM traffic and import to WFM for Forecasting:
https://zcxdemos.com/wfmdata.php
`;

// Types matching backend
interface RequirementsDocument {
  id: string;
  tenantId: string;
  name?: string;
  rawContent: string;
  status: string;
  zoomAccountId?: string;
  createdAt: string;
  updatedAt: string;
}

interface Requirement {
  id: string;
  documentId: string;
  area: string;
  section?: string;
  description: string;
  /** Original text from the document that this requirement was extracted from */
  sourceText?: string;
  priority: number;
  estimatedComplexity?: number;
  researchStatus: string;
  researchResults?: {
    endpoints: Array<{ method: string; path: string; description: string }>;
    configSteps: Array<{ order: number; action: string; endpoint?: string }>;
    verificationSteps?: Array<{
      order: number;
      action: string;
      expectedOutcome: string;
      strategy: string;
      critical?: boolean;
    }>;
    references?: Array<{
      url: string;
      title: string;
      type: string;
      snippet?: string;
    }>;
    prerequisites?: string[];
    confidence: number;
    warnings: string[];
    blockers: string[];
  };
  executionStatus: string;
  verificationStatus: string;
  createdAt: string;
}

interface PlanningStreamEvent {
  type: string;
  [key: string]: unknown;
}

interface BuildPhase {
  number: number;
  name: string;
  description?: string;
  requirementIds: string[];
  isCheckpoint: boolean;
  status: string;
  estimatedApiCalls?: number;
  components?: string[];
  dependsOnPhases?: number[];
  dependencyReason?: string;
}

interface BuildPlan {
  id: string;
  documentId: string;
  status: string;
  phases: BuildPhase[];
  currentPhase: number;
}

interface RequirementDependency {
  dependentId: string;
  dependencyId: string;
  dependencyType: 'hard' | 'soft';
  reason?: string;
}

// Execution step types (config step level)
interface ExecutionStep {
  id: string;
  buildPlanId: string;
  requirementId: string;
  executionOrder: number;
  action: string;
  endpoint?: string;
  method?: string;
  resourcesCreated: string[];
  resourcesRequired: string[];
  dependsOn: string[];
  status: string;
}

interface ExecutionWave {
  waveNumber: number;
  stepIds: string[];
  status: string;
}

// Map document status to workflow step
function statusToWorkflowStep(status: string): WorkflowStep {
  switch (status) {
    case 'uploaded':
      return 'upload';
    case 'decomposing':
      return 'decompose';
    case 'researching':
      return 'research';
    case 'analyzing_dependencies':
    case 'planning':
    case 'ready':
      return 'plan';
    case 'executing':
      return 'configure';
    case 'verifying':
      return 'verify';
    case 'completed':
      return 'report';
    default:
      return 'upload';
  }
}

// Get completed steps from current status
function getCompletedSteps(status: string): WorkflowStep[] {
  const steps: WorkflowStep[] = [];
  const stepOrder: WorkflowStep[] = ['upload', 'decompose', 'research', 'plan', 'configure', 'verify', 'report'];
  const currentStep = statusToWorkflowStep(status);
  const currentIndex = stepOrder.indexOf(currentStep);

  for (let i = 0; i < currentIndex; i++) {
    steps.push(stepOrder[i]!);
  }

  return steps;
}

// Convert stream event to activity event
function streamEventToActivity(event: PlanningStreamEvent): ActivityEvent | null {
  const id = crypto.randomUUID();
  const timestamp = new Date();

  switch (event.type) {
    case 'decomposition_started':
      return { id, timestamp, type: event.type, message: 'Starting document decomposition...', status: 'progress' };

    case 'area_identified':
      return {
        id, timestamp, type: event.type,
        message: `Found area: ${event.area} (${event.requirementCount} items)`,
        status: 'info'
      };

    case 'requirement_extracted':
      return {
        id, timestamp, type: event.type,
        message: `Extracted: ${(event.requirement as Requirement)?.description?.substring(0, 50)}...`,
        status: 'success'
      };

    case 'decomposition_completed':
      return {
        id, timestamp, type: event.type,
        message: `Decomposition complete: ${event.totalRequirements} requirements found`,
        status: 'success'
      };

    case 'research_started':
      return { id, timestamp, type: event.type, message: `Researching ${event.totalRequirements} requirements...`, status: 'progress' };

    case 'requirement_research_started':
      return {
        id, timestamp, type: event.type,
        message: `Researching: ${(event.description as string)?.substring(0, 40)}...`,
        status: 'progress'
      };

    case 'requirement_research_completed':
      return {
        id, timestamp, type: event.type,
        message: `Research complete (${(event.endpoints as string[])?.length ?? 0} endpoints, ${event.confidence}% confidence)`,
        status: 'success'
      };

    case 'requirement_research_failed':
      return {
        id, timestamp, type: event.type,
        message: `Research failed: ${event.error}`,
        status: 'error'
      };

    case 'research_completed':
      return {
        id, timestamp, type: event.type,
        message: `Research complete: ${event.researched} done, ${event.failed} failed`,
        status: event.failed ? 'warning' : 'success'
      };

    case 'error':
      return { id, timestamp, type: event.type, message: event.message as string, status: 'error' };

    case 'status_changed':
      return { id, timestamp, type: event.type, message: `Status changed to: ${event.status}`, status: 'info' };

    default:
      return { id, timestamp, type: event.type, message: JSON.stringify(event), status: 'info' };
  }
}

interface ZoomConnection {
  accountId: string;
  accountName: string;
}

interface SetupWizardProps {
  accountId?: string;
  onAccountChange?: (accountId: string) => void;
  connections?: ZoomConnection[];
}

export function SetupWizard({ accountId, onAccountChange, connections = [] }: SetupWizardProps) {
  // State
  const [documentId, setDocumentId] = useState<string | null>(null);
  const [document, setDocument] = useState<RequirementsDocument | null>(null);
  const [requirements, setRequirements] = useState<Requirement[]>([]);
  const [selectedRequirementId, setSelectedRequirementId] = useState<string | null>(null);
  const [activities, setActivities] = useState<ActivityEvent[]>([]);
  const [expandedAreas, setExpandedAreas] = useState<string[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isResuming, setIsResuming] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [recentDocuments, setRecentDocuments] = useState<RequirementsDocument[]>([]);
  const [buildPlan, setBuildPlan] = useState<BuildPlan | null>(null);
  const [dependencies, setDependencies] = useState<RequirementDependency[]>([]);
  const [executionSteps, setExecutionSteps] = useState<ExecutionStep[]>([]);
  const [executionWaves, setExecutionWaves] = useState<ExecutionWave[]>([]);
  const [isRegeneratingDag, setIsRegeneratingDag] = useState(false);
  const [viewMode, setViewMode] = useState<'list' | 'graph'>('list');
  const [showDagModal, setShowDagModal] = useState(false);
  const [dagZoom, setDagZoom] = useState(0.5); // Start zoomed out

  // Refs
  const fileInputRef = useRef<HTMLInputElement>(null);
  const streamRef = useRef<WebSocket | null>(null);

  // Load recent documents on mount
  useEffect(() => {
    const loadRecentDocuments = async () => {
      try {
        const response = await fetch(`${API_BASE}/planner/documents?tenantId=${accountId ?? 'dev-tenant'}&limit=5`);
        if (response.ok) {
          const data = await response.json();
          setRecentDocuments(data.documents ?? []);
        }
      } catch (err) {
        console.error('Failed to load recent documents:', err);
      }
    };
    loadRecentDocuments();
  }, [accountId]);

  // Resume document workflow
  const handleResume = useCallback(async (docId?: string) => {
    const targetId = docId ?? documentId;
    if (!targetId) return;

    setIsResuming(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE}/planner/documents/${targetId}/resume`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!response.ok) {
        throw new Error(`Resume failed: ${response.statusText}`);
      }

      const data = await response.json();

      // If we're resuming a different document, switch to it
      if (docId && docId !== documentId) {
        setDocumentId(docId);
        setActivities([]);
        setRequirements([]);
        setExpandedAreas([]);
        setSelectedRequirementId(null);
      }

      // Add activity
      setActivities(prev => [...prev, {
        id: crypto.randomUUID(),
        timestamp: new Date(),
        type: 'document_resumed',
        message: data.message,
        status: data.resumed ? 'success' : 'info'
      }]);

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Resume failed');
    } finally {
      setIsResuming(false);
    }
  }, [documentId]);

  // Approve and start execution
  const handleApprove = useCallback(async () => {
    if (!buildPlan?.id) {
      setError('No build plan to approve');
      return;
    }

    setIsApproving(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE}/planner/build-plans/${buildPlan.id}/approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ checkpointMode: 'issues_only' }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Approve failed: ${response.statusText}`);
      }

      // Add activity
      setActivities(prev => [...prev, {
        id: crypto.randomUUID(),
        timestamp: new Date(),
        type: 'execution_started',
        message: 'Build plan approved - starting execution...',
        status: 'success'
      }]);

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Approve failed');
    } finally {
      setIsApproving(false);
    }
  }, [buildPlan?.id]);

  // Upload document
  const handleUpload = useCallback(async (content: string, name?: string) => {
    setIsUploading(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE}/planner/documents`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // Pass accountId as both tenantId (for document organization) and zoomAccountId (for execution)
        body: JSON.stringify({ content, name, tenantId: accountId, zoomAccountId: accountId }),
      });

      if (!response.ok) {
        throw new Error(`Upload failed: ${response.statusText}`);
      }

      const data = await response.json();
      setDocumentId(data.documentId);

      // Add activity
      setActivities(prev => [...prev, {
        id: crypto.randomUUID(),
        timestamp: new Date(),
        type: 'document_uploaded',
        message: `Document uploaded: ${name ?? 'Unnamed'}`,
        status: 'success'
      }]);

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setIsUploading(false);
    }
  }, [accountId]);

  // Handle file selection
  const handleFileSelect = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target?.result as string;
      handleUpload(content, file.name);
    };
    reader.readAsText(file);
  }, [handleUpload]);

  // Handle paste
  const [pasteContent, setPasteContent] = useState('');
  const handlePaste = useCallback(() => {
    if (pasteContent.trim()) {
      handleUpload(pasteContent, 'Pasted content');
    }
  }, [pasteContent, handleUpload]);

  // Load sample document
  const loadSample = useCallback(() => {
    setPasteContent(ZOOMLLY_JAMS_SAMPLE);
  }, []);

  // Connect to stream when we have a document
  useEffect(() => {
    if (!documentId) return;

    // Connect to WebSocket stream
    const ws = new WebSocket(`${WS_BASE}/planner/documents/${documentId}/stream`);
    streamRef.current = ws;

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.event) {
          const evt = data.event;
          const activity = streamEventToActivity(evt);
          if (activity) {
            setActivities(prev => [...prev, activity]);
          }
        }
      } catch (err) {
        console.error('Failed to parse stream event:', err);
      }
    };

    ws.onerror = (event) => {
      console.error('WebSocket error:', event);
    };

    return () => {
      ws.close();
      streamRef.current = null;
    };
  }, [documentId]);

  // Poll for document, requirements, and build plan updates
  useEffect(() => {
    if (!documentId) return;

    const fetchDocument = async () => {
      try {
        const response = await fetch(`${API_BASE}/planner/documents/${documentId}`);
        if (response.ok) {
          const data = await response.json();
          setDocument(data.document);
          setRequirements(data.requirements);

          // Sync account if document has a different zoomAccountId
          if (data.document.zoomAccountId && data.document.zoomAccountId !== accountId) {
            onAccountChange?.(data.document.zoomAccountId);
          }

          // Auto-expand first phase/area when requirements arrive
          if (data.requirements.length > 0 && expandedAreas.length === 0) {
            // Will be updated once build plan arrives with phase info
            if (buildPlan?.phases?.[0]) {
              setExpandedAreas([`Phase ${buildPlan.phases[0].number}: ${buildPlan.phases[0].name}`]);
            } else {
              const firstArea = data.requirements[0]?.area;
              if (firstArea) {
                setExpandedAreas([firstArea]);
              }
            }
          }
        }
      } catch (err) {
        console.error('Failed to fetch document:', err);
      }
    };

    const fetchBuildPlan = async () => {
      try {
        const response = await fetch(`${API_BASE}/planner/documents/${documentId}/build-plan`);
        if (response.ok) {
          const data = await response.json();
          if (data.buildPlan) {
            setBuildPlan(data.buildPlan);

            // Fetch execution steps if we have a build plan
            try {
              const execResponse = await fetch(`${API_BASE}/planner/build-plans/${data.buildPlan.id}/execution-steps`);
              if (execResponse.ok) {
                const execData = await execResponse.json();
                if (execData.steps) {
                  setExecutionSteps(execData.steps);
                }
                if (execData.waves) {
                  setExecutionWaves(execData.waves);
                }
              }
            } catch (execErr) {
              console.error('Failed to fetch execution steps:', execErr);
            }
          }
          if (data.dependencies) {
            setDependencies(data.dependencies);
          }
        }
      } catch (err) {
        console.error('Failed to fetch build plan:', err);
      }
    };

    // Initial fetch
    fetchDocument();
    fetchBuildPlan();

    // Poll every 2 seconds
    const interval = setInterval(() => {
      fetchDocument();
      fetchBuildPlan();
    }, 2000);

    return () => clearInterval(interval);
  }, [documentId, expandedAreas.length]);

  // Get selected requirement
  const selectedRequirement = requirements.find(r => r.id === selectedRequirementId);

  // Calculate overall score
  const calculateScore = () => {
    if (requirements.length === 0) return 0;
    const passed = requirements.filter(r => r.verificationStatus === 'passed').length;
    return Math.round((passed / requirements.length) * 100);
  };

  // Map requirements to list items, sorted by build plan phase order
  const requirementItems: RequirementListItem[] = (() => {
    // If we have a build plan with valid phases, order by phases
    if (buildPlan?.phases && Array.isArray(buildPlan.phases) && buildPlan.phases.length > 0) {
      const orderedItems: RequirementListItem[] = [];
      const reqMap = new Map(requirements.map(r => [r.id, r]));

      for (const phase of buildPlan.phases) {
        // Defensive check: ensure requirementIds is an array
        const reqIds = Array.isArray(phase.requirementIds) ? phase.requirementIds : [];

        for (const reqId of reqIds) {
          const r = reqMap.get(reqId);
          if (r) {
            orderedItems.push({
              id: r.id,
              area: `Phase ${phase.number}: ${phase.name}`,
              section: r.section,
              description: r.description,
              researchStatus: r.researchStatus as RequirementListItem['researchStatus'],
              executionStatus: r.executionStatus as RequirementListItem['executionStatus'],
              verificationStatus: r.verificationStatus as RequirementListItem['verificationStatus'],
              estimatedComplexity: r.estimatedComplexity,
            });
            reqMap.delete(reqId); // Remove to avoid duplicates
          }
        }
      }

      // Add any remaining requirements not in phases
      for (const r of reqMap.values()) {
        orderedItems.push({
          id: r.id,
          area: r.area,
          section: r.section,
          description: r.description,
          researchStatus: r.researchStatus as RequirementListItem['researchStatus'],
          executionStatus: r.executionStatus as RequirementListItem['executionStatus'],
          verificationStatus: r.verificationStatus as RequirementListItem['verificationStatus'],
          estimatedComplexity: r.estimatedComplexity,
        });
      }

      return orderedItems;
    }

    // No build plan yet, use original area grouping
    return requirements.map(r => ({
      id: r.id,
      area: r.area,
      section: r.section,
      description: r.description,
      researchStatus: r.researchStatus as RequirementListItem['researchStatus'],
      executionStatus: r.executionStatus as RequirementListItem['executionStatus'],
      verificationStatus: r.verificationStatus as RequirementListItem['verificationStatus'],
      estimatedComplexity: r.estimatedComplexity,
    }));
  })();

  // Current workflow state
  const currentStep = document ? statusToWorkflowStep(document.status) : 'upload';
  const completedSteps = document ? getCompletedSteps(document.status) : [];

  const activeConnection = connections.find(c => c.accountId === accountId);

  return (
    <div className="flex flex-col h-full bg-background text-foreground">
      {/* Header - matches Ask Zac header height (h-14) */}
      <div className="bg-card border-b border-border px-6 h-14 flex items-center">
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center gap-6">
            <h1 className="text-xl font-bold text-foreground">Setup Wizard</h1>
            {document && (
              <span className="text-sm text-muted-foreground">
                {document.name ?? 'Pasted content'} - {requirements.length} requirements
              </span>
            )}
            {/* Account Selector */}
            {connections.length > 0 && (
              <div className="relative group">
                <button className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-secondary hover:bg-secondary/80 transition-colors">
                  <div className="w-2 h-2 rounded-full bg-green-500" />
                  <span className="font-medium text-sm">{activeConnection?.accountName || 'Select account'}</span>
                  <svg className="w-4 h-4 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {/* Dropdown */}
                <div className="absolute left-0 top-full mt-1 w-64 bg-card border border-border rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
                  <div className="py-1">
                    {connections.map((conn) => (
                      <button
                        key={conn.accountId}
                        onClick={() => onAccountChange?.(conn.accountId)}
                        className="w-full flex items-center gap-3 px-4 py-2 text-left hover:bg-secondary/50 transition-colors"
                      >
                        <div className={`w-2 h-2 rounded-full ${conn.accountId === accountId ? 'bg-green-500' : 'bg-muted-foreground/30'}`} />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium truncate">{conn.accountName}</div>
                          <div className="text-xs text-muted-foreground truncate">{conn.accountId}</div>
                        </div>
                        {conn.accountId === accountId && (
                          <svg className="w-4 h-4 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="flex items-center gap-4">
            {document && (
              <>
                <span className={`text-lg font-bold ${
                  calculateScore() >= 80 ? 'text-green-400' :
                  calculateScore() >= 60 ? 'text-yellow-400' : 'text-red-400'
                }`}>
                  Score: {calculateScore()}%
                </span>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                  document.status === 'completed' ? 'bg-green-900/50 text-green-400' :
                  document.status === 'failed' ? 'bg-red-900/50 text-red-400' :
                  'bg-primary/20 text-primary'
                }`}>
                  {document.status}
                </span>
                {/* Resume button for interrupted workflows */}
                {['failed', 'uploaded', 'decomposing', 'researching', 'analyzing_dependencies'].includes(document.status) && (
                  <button
                    className="px-3 py-1 bg-yellow-600 hover:bg-yellow-500 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
                    onClick={() => handleResume()}
                    disabled={isResuming}
                  >
                    {isResuming ? 'Resuming...' : 'Resume'}
                  </button>
                )}
                {/* Export and Approve buttons when build plan is ready */}
                {document.status === 'ready' && buildPlan && (
                  <>
                    <button
                      className="px-3 py-1 bg-secondary hover:bg-secondary/80 text-foreground text-sm font-medium rounded-lg transition-colors border border-border flex items-center gap-1.5"
                      onClick={() => setShowDagModal(true)}
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
                      </svg>
                      View DAG
                    </button>
                    <button
                      className="px-3 py-1 bg-secondary hover:bg-secondary/80 text-foreground text-sm font-medium rounded-lg transition-colors border border-border"
                      onClick={async () => {
                        try {
                          const response = await fetch(`${API_BASE}/planner/documents/${documentId}/export?format=csv`);
                          const data = await response.json();
                          const blob = new Blob([data.content], { type: 'text/csv' });
                          const url = URL.createObjectURL(blob);
                          const link = window.document.createElement('a');
                          link.href = url;
                          link.download = data.filename || 'build-plan.csv';
                          link.click();
                          URL.revokeObjectURL(url);
                        } catch (err) {
                          console.error('Failed to export plan:', err);
                        }
                      }}
                    >
                      Export Plan
                    </button>
                    <button
                      className="px-3 py-1 bg-green-600 hover:bg-green-500 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
                      onClick={handleApprove}
                      disabled={isApproving}
                    >
                      {isApproving ? 'Starting...' : 'Approve & Execute'}
                    </button>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Workflow Progress */}
      <div className="px-6 py-4 bg-secondary/30 border-b border-border">
        <WorkflowProgress currentStep={currentStep} completedSteps={completedSteps} />
      </div>

      {/* Main content - 3 column layout */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left column - Requirements or Upload */}
        <div className="w-1/3 border-r border-border overflow-y-auto p-4">
          {!documentId ? (
            /* Upload panel */
            <div className="space-y-6">
              <div className="bg-card border border-border rounded-lg p-6">
                <h2 className="text-lg font-medium text-foreground mb-4">Upload Requirements Document</h2>

                {/* File upload */}
                <div
                  className="border-2 border-dashed border-border rounded-lg p-6 text-center hover:border-muted-foreground transition-colors cursor-pointer"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <svg className="w-10 h-10 mx-auto text-muted-foreground mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  <p className="text-sm text-muted-foreground mb-1">Drop a file or click to browse</p>
                  <p className="text-xs text-muted-foreground/70">.md, .txt files</p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".md,.txt"
                    className="hidden"
                    onChange={handleFileSelect}
                  />
                </div>

                <div className="my-4 flex items-center">
                  <div className="flex-1 border-t border-border" />
                  <span className="px-3 text-xs text-muted-foreground">or paste</span>
                  <div className="flex-1 border-t border-border" />
                </div>

                {/* Paste area header with load sample button */}
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-muted-foreground">Requirements</span>
                  <button
                    type="button"
                    className="text-xs text-primary hover:text-primary/80 transition-colors"
                    onClick={loadSample}
                  >
                    Load sample
                  </button>
                </div>

                {/* Paste area */}
                <textarea
                  className="w-full h-32 bg-background border border-border rounded-lg p-3 text-foreground text-sm resize-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="Paste requirements here..."
                  value={pasteContent}
                  onChange={(e) => setPasteContent(e.target.value)}
                />

                <button
                  className="mt-3 w-full py-2 px-4 bg-primary hover:bg-primary/90 text-primary-foreground font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                  onClick={handlePaste}
                  disabled={!pasteContent.trim() || isUploading}
                >
                  {isUploading ? 'Uploading...' : 'Upload & Start'}
                </button>

                {error && (
                  <div className="mt-3 p-3 bg-red-900/50 border border-red-700 rounded-lg text-red-400 text-xs">
                    {error}
                  </div>
                )}
              </div>

              {/* Recent Documents */}
              {recentDocuments.length > 0 && (
                <div className="bg-card border border-border rounded-lg p-4 mt-4">
                  <h3 className="text-sm font-medium text-foreground mb-3">Recent Documents</h3>
                  <div className="space-y-2">
                    {recentDocuments.map(doc => (
                      <div
                        key={doc.id}
                        className="flex items-center justify-between p-2 bg-secondary/50 rounded-lg hover:bg-secondary transition-colors"
                      >
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-foreground truncate">
                            {doc.name ?? 'Unnamed Document'}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {doc.status} - {new Date(doc.createdAt).toLocaleDateString()}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 ml-2">
                          {doc.status !== 'completed' && (
                            <button
                              className="px-2 py-1 bg-yellow-600 hover:bg-yellow-500 text-white text-xs font-medium rounded transition-colors disabled:opacity-50"
                              onClick={() => handleResume(doc.id)}
                              disabled={isResuming}
                            >
                              Resume
                            </button>
                          )}
                          <button
                            className="px-2 py-1 bg-primary hover:bg-primary/90 text-primary-foreground text-xs font-medium rounded transition-colors"
                            onClick={() => {
                              setDocumentId(doc.id);
                              setActivities([]);
                            }}
                          >
                            View
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            /* Requirements list or Graph view */
            <div className="flex flex-col h-full">
              {/* View Toggle Header */}
              {buildPlan?.phases && buildPlan.phases.length > 0 && (
                <div className="flex-shrink-0 mb-3 flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">
                    {buildPlan.phases.length} phases, {requirements.length} requirements
                  </span>
                  <div className="flex items-center gap-1 bg-secondary rounded-lg p-0.5">
                    <button
                      className={`px-2 py-1 text-xs rounded transition-colors ${
                        viewMode === 'list'
                          ? 'bg-background text-foreground shadow-sm'
                          : 'text-muted-foreground hover:text-foreground'
                      }`}
                      onClick={() => setViewMode('list')}
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                      </svg>
                    </button>
                    <button
                      className={`px-2 py-1 text-xs rounded transition-colors ${
                        viewMode === 'graph'
                          ? 'bg-background text-foreground shadow-sm'
                          : 'text-muted-foreground hover:text-foreground'
                      }`}
                      onClick={() => setViewMode('graph')}
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
                      </svg>
                    </button>
                  </div>
                </div>
              )}

              {/* Content */}
              <div className="flex-1 overflow-y-auto">
                {viewMode === 'list' ? (
                  <RequirementsList
                    requirements={requirementItems}
                    selectedId={selectedRequirementId ?? undefined}
                    onSelect={setSelectedRequirementId}
                    expandedAreas={expandedAreas}
                    onToggleArea={(area) => {
                      setExpandedAreas(prev =>
                        prev.includes(area) ? prev.filter(a => a !== area) : [...prev, area]
                      );
                    }}
                  />
                ) : (
                  /* DAG Dependency Graph */
                  (() => {
                    // Build node map with positions
                    const nodeSize = 32;
                    const nodeSpacingX = 80;
                    const nodeSpacingY = 50;
                    const padding = 20;

                    // Create nodes with step numbers
                    const nodes: Array<{
                      id: string;
                      step: number;
                      phase: number;
                      desc: string;
                      status: string;
                      deps: string[];
                      depth: number;
                      x: number;
                      y: number;
                    }> = [];

                    let stepNum = 0;
                    for (const phase of buildPlan?.phases ?? []) {
                      for (const reqId of phase.requirementIds) {
                        stepNum++;
                        const req = requirements.find(r => r.id === reqId);
                        const reqDeps = dependencies.filter(d => d.dependentId === reqId).map(d => d.dependencyId);
                        nodes.push({
                          id: reqId,
                          step: stepNum,
                          phase: phase.number,
                          desc: req?.description ?? '',
                          status: req?.executionStatus ?? 'pending',
                          deps: reqDeps,
                          depth: 0,
                          x: 0,
                          y: 0,
                        });
                      }
                    }

                    // Calculate depth (longest path from root)
                    const nodeMap = new Map(nodes.map(n => [n.id, n]));
                    const calcDepth = (id: string, visited = new Set<string>()): number => {
                      if (visited.has(id)) return 0;
                      visited.add(id);
                      const node = nodeMap.get(id);
                      if (!node || node.deps.length === 0) return 0;
                      return 1 + Math.max(...node.deps.map(d => calcDepth(d, visited)));
                    };

                    for (const node of nodes) {
                      node.depth = calcDepth(node.id);
                    }

                    // Group by depth
                    const maxDepth = Math.max(...nodes.map(n => n.depth), 0);
                    const columns: typeof nodes[] = Array.from({ length: maxDepth + 1 }, () => []);
                    for (const node of nodes) {
                      columns[node.depth]?.push(node);
                    }

                    // Calculate positions
                    const maxNodesInCol = Math.max(...columns.map(c => c.length), 1);
                    const svgWidth = (maxDepth + 1) * nodeSpacingX + padding * 2;
                    const svgHeight = maxNodesInCol * nodeSpacingY + padding * 2;

                    for (let col = 0; col < columns.length; col++) {
                      const colNodes = columns[col] ?? [];
                      const colHeight = colNodes.length * nodeSpacingY;
                      const startY = (svgHeight - colHeight) / 2 + nodeSpacingY / 2;

                      colNodes.forEach((node, idx) => {
                        node.x = padding + col * nodeSpacingX + nodeSize / 2;
                        node.y = startY + idx * nodeSpacingY;
                      });
                    }

                    // Generate edges
                    const edges: Array<{ from: typeof nodes[0]; to: typeof nodes[0]; type: string }> = [];
                    for (const node of nodes) {
                      for (const depId of node.deps) {
                        const depNode = nodeMap.get(depId);
                        if (depNode) {
                          const dep = dependencies.find(d => d.dependentId === node.id && d.dependencyId === depId);
                          edges.push({ from: depNode, to: node, type: dep?.dependencyType ?? 'hard' });
                        }
                      }
                    }

                    return (
                      <div className="relative" style={{ minHeight: Math.max(svgHeight, 300) }}>
                        <svg
                          width="100%"
                          height={Math.max(svgHeight, 300)}
                          viewBox={`0 0 ${Math.max(svgWidth, 300)} ${Math.max(svgHeight, 300)}`}
                          className="overflow-visible"
                        >
                          {/* Draw edges first (behind nodes) */}
                          <defs>
                            <marker
                              id="arrowhead"
                              markerWidth="6"
                              markerHeight="4"
                              refX="5"
                              refY="2"
                              orient="auto"
                            >
                              <polygon points="0 0, 6 2, 0 4" fill="#6b7280" />
                            </marker>
                            <marker
                              id="arrowhead-hard"
                              markerWidth="6"
                              markerHeight="4"
                              refX="5"
                              refY="2"
                              orient="auto"
                            >
                              <polygon points="0 0, 6 2, 0 4" fill="#f97316" />
                            </marker>
                          </defs>

                          {edges.map((edge, idx) => {
                            const startX = edge.from.x + nodeSize / 2;
                            const startY = edge.from.y;
                            const endX = edge.to.x - nodeSize / 2 - 6;
                            const endY = edge.to.y;

                            // Bezier curve
                            const midX = (startX + endX) / 2;
                            const path = `M ${startX} ${startY} C ${midX} ${startY}, ${midX} ${endY}, ${endX} ${endY}`;

                            return (
                              <path
                                key={idx}
                                d={path}
                                fill="none"
                                stroke={edge.type === 'hard' ? '#f97316' : '#eab308'}
                                strokeWidth={1.5}
                                strokeOpacity={0.6}
                                markerEnd={edge.type === 'hard' ? 'url(#arrowhead-hard)' : 'url(#arrowhead)'}
                              />
                            );
                          })}

                          {/* Draw nodes */}
                          {nodes.map((node) => {
                            const isSelected = selectedRequirementId === node.id;
                            const fillColor = node.status === 'completed'
                              ? '#22c55e'
                              : node.status === 'in_progress'
                              ? '#3b82f6'
                              : node.status === 'failed'
                              ? '#ef4444'
                              : isSelected
                              ? '#8b5cf6'
                              : '#6366f1';

                            return (
                              <g
                                key={node.id}
                                className="cursor-pointer"
                                onClick={() => setSelectedRequirementId(node.id)}
                              >
                                {/* Node circle */}
                                <circle
                                  cx={node.x}
                                  cy={node.y}
                                  r={nodeSize / 2}
                                  fill={fillColor}
                                  stroke={isSelected ? '#fff' : 'transparent'}
                                  strokeWidth={2}
                                  className="transition-all hover:opacity-80"
                                />
                                {/* Step number */}
                                <text
                                  x={node.x}
                                  y={node.y}
                                  textAnchor="middle"
                                  dominantBaseline="central"
                                  fill="white"
                                  fontSize="11"
                                  fontWeight="bold"
                                  className="pointer-events-none"
                                >
                                  {node.status === 'completed' ? '✓' : node.step}
                                </text>
                                {/* Tooltip on hover - phase indicator */}
                                <title>{`#${node.step} (P${node.phase}): ${node.desc.substring(0, 100)}`}</title>
                              </g>
                            );
                          })}
                        </svg>

                        {/* Legend */}
                        <div className="absolute bottom-2 left-2 flex items-center gap-4 text-[10px] text-muted-foreground bg-background/80 rounded px-2 py-1">
                          <div className="flex items-center gap-1">
                            <div className="w-3 h-3 rounded-full bg-[#6366f1]" />
                            <span>Pending</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <div className="w-3 h-3 rounded-full bg-[#3b82f6]" />
                            <span>In Progress</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <div className="w-3 h-3 rounded-full bg-[#22c55e]" />
                            <span>Complete</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <div className="w-2 h-0.5 bg-orange-500" />
                            <span>Hard Dep</span>
                          </div>
                        </div>

                        {/* Selected node info */}
                        {selectedRequirementId && (() => {
                          const node = nodeMap.get(selectedRequirementId);
                          if (!node) return null;
                          return (
                            <div className="absolute top-2 left-2 right-2 bg-card border border-border rounded-lg p-2 text-xs">
                              <div className="flex items-center gap-2">
                                <span className="font-bold text-primary">#{node.step}</span>
                                <span className="text-muted-foreground">Phase {node.phase}</span>
                              </div>
                              <p className="text-foreground mt-1 line-clamp-2">{node.desc}</p>
                            </div>
                          );
                        })()}
                      </div>
                    );
                  })()
                )}
              </div>
            </div>
          )}
        </div>

        {/* Middle column - Requirement Detail panel */}
        <div className="w-1/3 border-r border-border overflow-y-auto p-4">
          {selectedRequirement ? (
            <div className="bg-card border border-border rounded-lg p-4">
              <h3 className="text-sm font-medium text-foreground mb-2">
                {selectedRequirement.description}
              </h3>
              <div className="flex items-center gap-2 text-xs text-muted-foreground mb-4">
                <span>{selectedRequirement.area}</span>
                {selectedRequirement.section && (
                  <>
                    <span>/</span>
                    <span>{selectedRequirement.section}</span>
                  </>
                )}
              </div>

              {/* Source text - original text from document */}
              {selectedRequirement.sourceText && (
                <div className="mb-4 p-3 bg-secondary/50 rounded-lg border border-border">
                  <h4 className="text-xs font-medium text-foreground/80 mb-1">Original Text</h4>
                  <p className="text-xs text-muted-foreground italic">
                    "{selectedRequirement.sourceText}"
                  </p>
                </div>
              )}

              {/* Research results */}
              {selectedRequirement.researchResults && (
                <div className="space-y-4">
                  {selectedRequirement.researchResults.endpoints && selectedRequirement.researchResults.endpoints.length > 0 && (
                  <div>
                    <h4 className="text-xs font-medium text-foreground/80 mb-2">Endpoints</h4>
                    <div className="space-y-1">
                      {selectedRequirement.researchResults.endpoints.map((ep, idx) => (
                        <div key={idx} className="flex items-center gap-2 text-xs">
                          <span className={`px-1.5 py-0.5 rounded font-mono ${
                            ep.method === 'POST' ? 'bg-green-900/50 text-green-400' :
                            ep.method === 'PATCH' ? 'bg-yellow-900/50 text-yellow-400' :
                            ep.method === 'DELETE' ? 'bg-red-900/50 text-red-400' :
                            'bg-blue-900/50 text-blue-400'
                          }`}>
                            {ep.method}
                          </span>
                          <code className="text-muted-foreground truncate">{ep.path}</code>
                        </div>
                      ))}
                    </div>
                  </div>
                  )}

                  {selectedRequirement.researchResults.prerequisites && selectedRequirement.researchResults.prerequisites.length > 0 && (
                    <div>
                      <h4 className="text-xs font-medium text-foreground/80 mb-2">Prerequisites</h4>
                      <ul className="space-y-1 text-xs">
                        {selectedRequirement.researchResults.prerequisites.map((prereq, idx) => (
                          <li key={idx} className="flex items-start gap-2">
                            <span className="text-orange-400 mt-0.5">•</span>
                            <span className="text-muted-foreground">{prereq}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {selectedRequirement.researchResults.configSteps && selectedRequirement.researchResults.configSteps.length > 0 && (
                    <div>
                      <h4 className="text-xs font-medium text-foreground/80 mb-2">Configuration Steps</h4>
                      <ol className="space-y-2 text-xs">
                        {selectedRequirement.researchResults.configSteps.map((step, idx) => (
                          <li key={idx} className="flex gap-2">
                            <span className="text-muted-foreground/60 font-mono">{step.order || idx + 1}.</span>
                            <div>
                              <span className="text-muted-foreground">{step.action}</span>
                              {step.endpoint && (
                                <code className="ml-2 text-xs text-primary/70">{step.endpoint}</code>
                              )}
                            </div>
                          </li>
                        ))}
                      </ol>
                    </div>
                  )}

                  {selectedRequirement.researchResults.verificationSteps && selectedRequirement.researchResults.verificationSteps.length > 0 && (
                    <div>
                      <h4 className="text-xs font-medium text-foreground/80 mb-2">Verification Steps</h4>
                      <ol className="space-y-2 text-xs">
                        {selectedRequirement.researchResults.verificationSteps.map((step, idx) => (
                          <li key={idx} className="flex gap-2">
                            <span className="text-muted-foreground/60 font-mono">{step.order || idx + 1}.</span>
                            <div>
                              <span className={step.critical ? 'text-yellow-400' : 'text-muted-foreground'}>
                                {step.action}
                                {step.critical && <span className="ml-1 text-yellow-500">*</span>}
                              </span>
                              <p className="text-muted-foreground/70 text-xs mt-0.5">{step.expectedOutcome}</p>
                            </div>
                          </li>
                        ))}
                      </ol>
                    </div>
                  )}

                  {selectedRequirement.researchResults.references && selectedRequirement.researchResults.references.length > 0 && (
                    <div>
                      <h4 className="text-xs font-medium text-foreground/80 mb-2">References</h4>
                      <ul className="space-y-2 text-xs">
                        {selectedRequirement.researchResults.references.map((ref, idx) => (
                          <li key={idx}>
                            <a
                              href={ref.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-primary hover:underline flex items-start gap-1"
                            >
                              <svg className="w-3 h-3 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                              </svg>
                              <span className="break-words">{ref.title}</span>
                            </a>
                            <span className="text-muted-foreground/50 text-xs ml-4">[{ref.type}]</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {selectedRequirement.researchResults.warnings && selectedRequirement.researchResults.warnings.length > 0 && (
                    <div>
                      <h4 className="text-xs font-medium text-yellow-400 mb-2">Warnings</h4>
                      <ul className="list-disc list-inside space-y-1 text-xs text-yellow-300">
                        {selectedRequirement.researchResults.warnings.map((w, idx) => (
                          <li key={idx}>{w}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Confidence Score */}
                  {selectedRequirement.researchResults.confidence !== undefined && (
                    <div className="pt-2">
                      <div className="flex items-center justify-between text-xs mb-1">
                        <span className="text-muted-foreground">Confidence</span>
                        <span className={`font-medium ${
                          selectedRequirement.researchResults.confidence >= 0.8 ? 'text-green-400' :
                          selectedRequirement.researchResults.confidence >= 0.6 ? 'text-yellow-400' :
                          'text-red-400'
                        }`}>
                          {Math.round(selectedRequirement.researchResults.confidence * 100)}%
                        </span>
                      </div>
                      <div className="h-1.5 bg-secondary rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all ${
                            selectedRequirement.researchResults.confidence >= 0.8 ? 'bg-green-500' :
                            selectedRequirement.researchResults.confidence >= 0.6 ? 'bg-yellow-500' :
                            'bg-red-500'
                          }`}
                          style={{ width: `${selectedRequirement.researchResults.confidence * 100}%` }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Dependencies - what this requirement depends on and what depends on it */}
              {(() => {
                const dependsOn = dependencies.filter(d => d.dependentId === selectedRequirement.id);
                const requiredBy = dependencies.filter(d => d.dependencyId === selectedRequirement.id);

                if (dependsOn.length === 0 && requiredBy.length === 0) return null;

                return (
                  <div className="mt-4 space-y-3">
                    {/* What this requirement depends on */}
                    {dependsOn.length > 0 && (
                      <div className="p-3 bg-orange-900/20 border border-orange-700/50 rounded-lg">
                        <h4 className="text-xs font-medium text-orange-400 mb-2 flex items-center gap-1.5">
                          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                          </svg>
                          Depends On ({dependsOn.length})
                        </h4>
                        <ul className="space-y-2">
                          {dependsOn.map((dep, idx) => {
                            const depReq = requirements.find(r => r.id === dep.dependencyId);
                            return (
                              <li key={idx} className="text-xs">
                                <button
                                  className="text-left w-full hover:bg-orange-900/30 rounded p-1.5 -m-1.5 transition-colors"
                                  onClick={() => setSelectedRequirementId(dep.dependencyId)}
                                >
                                  <div className="flex items-start gap-2">
                                    <span className={`flex-shrink-0 px-1.5 py-0.5 rounded text-[10px] font-medium ${
                                      dep.dependencyType === 'hard'
                                        ? 'bg-red-900/50 text-red-400'
                                        : 'bg-yellow-900/50 text-yellow-400'
                                    }`}>
                                      {dep.dependencyType}
                                    </span>
                                    <div className="flex-1 min-w-0">
                                      <p className="text-orange-200 truncate">
                                        {depReq?.description ?? dep.dependencyId}
                                      </p>
                                      {dep.reason && (
                                        <p className="text-orange-400/70 text-[10px] mt-0.5">{dep.reason}</p>
                                      )}
                                    </div>
                                  </div>
                                </button>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    )}

                    {/* What depends on this requirement */}
                    {requiredBy.length > 0 && (
                      <div className="p-3 bg-blue-900/20 border border-blue-700/50 rounded-lg">
                        <h4 className="text-xs font-medium text-blue-400 mb-2 flex items-center gap-1.5">
                          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6" />
                          </svg>
                          Required By ({requiredBy.length})
                        </h4>
                        <ul className="space-y-2">
                          {requiredBy.map((dep, idx) => {
                            const depReq = requirements.find(r => r.id === dep.dependentId);
                            return (
                              <li key={idx} className="text-xs">
                                <button
                                  className="text-left w-full hover:bg-blue-900/30 rounded p-1.5 -m-1.5 transition-colors"
                                  onClick={() => setSelectedRequirementId(dep.dependentId)}
                                >
                                  <div className="flex items-start gap-2">
                                    <span className={`flex-shrink-0 px-1.5 py-0.5 rounded text-[10px] font-medium ${
                                      dep.dependencyType === 'hard'
                                        ? 'bg-red-900/50 text-red-400'
                                        : 'bg-yellow-900/50 text-yellow-400'
                                    }`}>
                                      {dep.dependencyType}
                                    </span>
                                    <div className="flex-1 min-w-0">
                                      <p className="text-blue-200 truncate">
                                        {depReq?.description ?? dep.dependentId}
                                      </p>
                                      {dep.reason && (
                                        <p className="text-blue-400/70 text-[10px] mt-0.5">{dep.reason}</p>
                                      )}
                                    </div>
                                  </div>
                                </button>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Status badges */}
              <div className="mt-4 pt-4 border-t border-border flex flex-wrap items-center gap-2">
                <span className={`text-xs px-2 py-0.5 rounded ${
                  selectedRequirement.researchStatus === 'complete' ? 'bg-green-900/50 text-green-400' :
                  selectedRequirement.researchStatus === 'failed' ? 'bg-red-900/50 text-red-400' :
                  selectedRequirement.researchStatus === 'researching' ? 'bg-blue-900/50 text-blue-400' :
                  'bg-secondary text-muted-foreground'
                }`}>
                  research: {selectedRequirement.researchStatus}
                </span>
                <span className={`text-xs px-2 py-0.5 rounded ${
                  selectedRequirement.executionStatus === 'completed' ? 'bg-green-900/50 text-green-400' :
                  selectedRequirement.executionStatus === 'failed' ? 'bg-red-900/50 text-red-400' :
                  selectedRequirement.executionStatus === 'in_progress' ? 'bg-blue-900/50 text-blue-400' :
                  'bg-secondary text-muted-foreground'
                }`}>
                  config: {selectedRequirement.executionStatus}
                </span>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
              Select a requirement to view details
            </div>
          )}
        </div>

        {/* Right column - Activity feed (real-time event log) */}
        <div className="w-1/3 overflow-hidden flex flex-col">
          <ActivityFeed events={activities} autoScroll={true} fullHeight={true} />
        </div>
      </div>

      {/* DAG Modal - Full screen config step graph */}
      {showDagModal && buildPlan?.phases && (
        <div className="fixed inset-0 z-50 bg-black/90 flex flex-col">
          {/* Minimal Header */}
          <div className="flex items-center justify-between px-4 py-2 bg-card/50 border-b border-border">
            <div className="flex items-center gap-4">
              <h2 className="text-sm font-medium text-foreground">Execution DAG - Config Steps</h2>
              <span className="text-xs text-muted-foreground">
                {executionSteps.length > 0
                  ? `${executionSteps.length} config steps • ${executionWaves.length} waves • Click node for details`
                  : `${requirements.length} requirements (no config steps yet)`
                }
              </span>
            </div>
            <div className="flex items-center gap-2">
              {/* Regenerate button */}
              <button
                onClick={async () => {
                  if (!buildPlan?.id || !documentId || isRegeneratingDag) return;
                  setIsRegeneratingDag(true);
                  try {
                    // First re-analyze dependencies
                    const depResponse = await fetch(`${API_BASE}/planner/documents/${documentId}/reanalyze-dependencies`, {
                      method: 'POST',
                    });
                    if (depResponse.ok) {
                      const depData = await depResponse.json();
                      if (depData.dependencies) setDependencies(depData.dependencies);
                      console.log(`Re-analyzed: ${depData.dependencies?.length ?? 0} dependencies found`);
                    }

                    // Then regenerate execution plan
                    const response = await fetch(`${API_BASE}/planner/build-plans/${buildPlan.id}/execution-steps/regenerate`, {
                      method: 'POST',
                    });
                    if (response.ok) {
                      const data = await response.json();
                      if (data.steps) setExecutionSteps(data.steps);
                      if (data.waves) setExecutionWaves(data.waves);
                    }
                  } catch (err) {
                    console.error('Failed to regenerate:', err);
                  } finally {
                    setIsRegeneratingDag(false);
                  }
                }}
                disabled={isRegeneratingDag}
                className="px-2 py-1 text-xs bg-orange-600 hover:bg-orange-500 disabled:bg-orange-800 disabled:cursor-wait rounded transition-colors text-white"
                title="Re-analyze dependencies and regenerate execution plan"
              >
                {isRegeneratingDag ? 'Regenerating...' : 'Regenerate'}
              </button>
              {/* Zoom controls */}
              <div className="flex items-center gap-1 bg-secondary/50 rounded px-1">
                <button
                  onClick={() => setDagZoom(z => Math.max(0.1, z - 0.1))}
                  className="p-1 hover:bg-secondary rounded transition-colors text-muted-foreground hover:text-foreground"
                  title="Zoom out"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                  </svg>
                </button>
                <span className="text-xs text-muted-foreground min-w-[3rem] text-center">
                  {Math.round(dagZoom * 100)}%
                </span>
                <button
                  onClick={() => setDagZoom(z => Math.min(2, z + 0.1))}
                  className="p-1 hover:bg-secondary rounded transition-colors text-muted-foreground hover:text-foreground"
                  title="Zoom in"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                </button>
                <button
                  onClick={() => setDagZoom(0.5)}
                  className="p-1 hover:bg-secondary rounded transition-colors text-xs text-muted-foreground hover:text-foreground"
                  title="Reset zoom"
                >
                  Fit
                </button>
              </div>
              <button
                onClick={() => setShowDagModal(false)}
                className="p-1.5 hover:bg-secondary rounded transition-colors"
              >
                <svg className="w-4 h-4 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Full DAG Canvas */}
          <div className="flex-1 overflow-auto p-4">
            {executionSteps.length > 0 ? (
              /* Wave-based layout for config steps */
              (() => {
                const nodeRadius = 14;
                const nodeGapX = 45;
                const waveGap = 280; // Wider to fit text labels
                const padding = 40;

                // Find max steps in any wave for height calculation
                const maxStepsInWave = Math.max(...executionWaves.map(w => w.stepIds.length), 1);
                const svgHeight = maxStepsInWave * nodeGapX + padding * 2;
                const svgWidth = executionWaves.length * waveGap + padding * 2;

                // Position steps by wave
                const positions = new Map<string, { x: number; y: number; wave: number }>();
                executionWaves.forEach((wave, waveIdx) => {
                  const waveSteps = wave.stepIds;
                  const waveHeight = waveSteps.length * nodeGapX;
                  const startY = (svgHeight - waveHeight) / 2 + nodeGapX / 2;

                  waveSteps.forEach((stepId, stepIdx) => {
                    positions.set(stepId, {
                      x: padding + waveIdx * waveGap + waveGap / 2,
                      y: startY + stepIdx * nodeGapX,
                      wave: wave.waveNumber,
                    });
                  });
                });

                // Scaled dimensions for proper scrolling
                const scaledWidth = svgWidth * dagZoom;
                const scaledHeight = (svgHeight + 30) * dagZoom;

                return (
                  <div
                    style={{
                      width: Math.max(scaledWidth, 100),
                      height: Math.max(scaledHeight, 100),
                    }}
                  >
                    <div
                      className="flex flex-col"
                      style={{
                        transform: `scale(${dagZoom})`,
                        transformOrigin: 'top left',
                      }}
                    >
                    {/* Wave labels */}
                    <div className="flex mb-2" style={{ width: svgWidth }}>
                      {executionWaves.map((wave, idx) => (
                        <div
                          key={wave.waveNumber}
                          className="text-center text-xs text-muted-foreground"
                          style={{ width: waveGap, marginLeft: idx === 0 ? padding : 0 }}
                        >
                          Wave {wave.waveNumber + 1}
                          <span className="text-muted-foreground/50 ml-1">({wave.stepIds.length})</span>
                        </div>
                      ))}
                    </div>

                    <svg
                      width={svgWidth}
                      height={svgHeight}
                      className="overflow-visible"
                    >
                      {/* Arrow marker */}
                      <defs>
                        <marker
                          id="step-arrow"
                          markerWidth="6"
                          markerHeight="5"
                          refX="5"
                          refY="2.5"
                          orient="auto"
                        >
                          <polygon points="0 0, 6 2.5, 0 5" fill="#f97316" fillOpacity={0.7} />
                        </marker>
                      </defs>

                      {/* Wave separator lines */}
                      {executionWaves.map((_, idx) => (
                        idx > 0 && (
                          <line
                            key={`wave-sep-${idx}`}
                            x1={padding + idx * waveGap}
                            y1={10}
                            x2={padding + idx * waveGap}
                            y2={svgHeight - 10}
                            stroke="#374151"
                            strokeWidth={1}
                            strokeDasharray="4 4"
                          />
                        )
                      ))}

                      {/* Draw dependency edges */}
                      {executionSteps.flatMap(step =>
                        step.dependsOn.map((depId, idx) => {
                          const from = positions.get(depId);
                          const to = positions.get(step.id);
                          if (!from || !to) return null;

                          const dx = to.x - from.x;
                          const dy = to.y - from.y;
                          const dist = Math.sqrt(dx * dx + dy * dy);
                          if (dist === 0) return null;

                          const startX = from.x + (dx / dist) * nodeRadius;
                          const startY = from.y + (dy / dist) * nodeRadius;
                          const endX = to.x - (dx / dist) * (nodeRadius + 5);
                          const endY = to.y - (dy / dist) * (nodeRadius + 5);

                          // Bezier curve for smoother lines
                          const midX = (startX + endX) / 2;
                          const path = `M ${startX} ${startY} C ${midX} ${startY}, ${midX} ${endY}, ${endX} ${endY}`;

                          return (
                            <path
                              key={`edge-${step.id}-${depId}-${idx}`}
                              d={path}
                              fill="none"
                              stroke="#f97316"
                              strokeWidth={1.5}
                              strokeOpacity={0.5}
                              markerEnd="url(#step-arrow)"
                            />
                          );
                        })
                      )}

                      {/* Draw step nodes */}
                      {executionSteps.map(step => {
                        const pos = positions.get(step.id);
                        if (!pos) return null;

                        const fillColor = step.status === 'completed'
                          ? '#22c55e'
                          : step.status === 'in_progress'
                          ? '#3b82f6'
                          : step.status === 'failed'
                          ? '#ef4444'
                          : step.status === 'ready'
                          ? '#8b5cf6'
                          : '#6366f1';

                        // Find the requirement this step belongs to
                        const req = requirements.find(r => r.id === step.requirementId);

                        // Short description - truncate action to 30 chars
                        const shortDesc = step.action.length > 35
                          ? step.action.substring(0, 35) + '...'
                          : step.action;

                        return (
                          <g
                            key={step.id}
                            className="cursor-pointer"
                            onClick={() => {
                              // Select the parent requirement
                              setSelectedRequirementId(step.requirementId);
                              setShowDagModal(false);
                            }}
                          >
                            {/* Node circle */}
                            <circle
                              cx={pos.x}
                              cy={pos.y}
                              r={nodeRadius}
                              fill={fillColor}
                              stroke="rgba(255,255,255,0.3)"
                              strokeWidth={1}
                              className="transition-all hover:stroke-white hover:stroke-2"
                            />
                            {/* Step number inside */}
                            <text
                              x={pos.x}
                              y={pos.y}
                              textAnchor="middle"
                              dominantBaseline="central"
                              fill="#fff"
                              fontSize={9}
                              fontWeight="bold"
                              className="pointer-events-none select-none"
                            >
                              {step.executionOrder}
                            </text>
                            {/* Step description label */}
                            <text
                              x={pos.x + nodeRadius + 6}
                              y={pos.y}
                              textAnchor="start"
                              dominantBaseline="central"
                              fill="#9ca3af"
                              fontSize={8}
                              className="pointer-events-none select-none"
                            >
                              {shortDesc}
                            </text>
                            {/* Tooltip with full details */}
                            <title>
                              {`Step #${step.executionOrder}: ${step.action}\n` +
                               `${step.method ? `${step.method} ${step.endpoint}` : ''}\n` +
                               `Creates: ${step.resourcesCreated.join(', ') || 'none'}\n` +
                               `Requires: ${step.resourcesRequired.join(', ') || 'none'}\n` +
                               `From: ${req?.description?.substring(0, 50) ?? step.requirementId}`}
                            </title>
                          </g>
                        );
                      })}
                    </svg>
                    </div>
                  </div>
                );
              })()
            ) : (
              /* Fallback: requirement-level DAG when no execution steps */
              (() => {
                const nodes: Array<{
                  id: string;
                  step: number;
                  phase: number;
                  desc: string;
                  status: string;
                  deps: string[];
                }> = [];

                let stepNum = 0;
                for (const phase of buildPlan.phases) {
                  for (const reqId of phase.requirementIds) {
                    stepNum++;
                    const req = requirements.find(r => r.id === reqId);
                    const reqDeps = dependencies.filter(d => d.dependentId === reqId).map(d => d.dependencyId);
                    nodes.push({
                      id: reqId,
                      step: stepNum,
                      phase: phase.number,
                      desc: req?.description ?? '',
                      status: req?.executionStatus ?? 'pending',
                      deps: reqDeps,
                    });
                  }
                }

                const nodeRadius = 14;
                const nodeGap = 50;
                const padding = 30;
                const availableWidth = Math.max(window.innerWidth - padding * 2, 800);
                const nodesPerRow = Math.floor(availableWidth / nodeGap);
                const numRows = Math.ceil(nodes.length / nodesPerRow);
                const svgWidth = nodesPerRow * nodeGap + padding * 2;
                const svgHeight = numRows * nodeGap + padding * 2;

                const positions = new Map<string, { x: number; y: number }>();
                nodes.forEach((node, idx) => {
                  const col = idx % nodesPerRow;
                  const row = Math.floor(idx / nodesPerRow);
                  positions.set(node.id, {
                    x: padding + col * nodeGap + nodeGap / 2,
                    y: padding + row * nodeGap + nodeGap / 2,
                  });
                });

                return (
                  <div className="text-center">
                    <p className="text-xs text-muted-foreground mb-4">
                      Showing requirement-level view (config steps not yet generated)
                    </p>
                    <svg width={svgWidth} height={svgHeight} className="w-full">
                      <defs>
                        <marker id="dag-arrow" markerWidth="6" markerHeight="5" refX="5" refY="2.5" orient="auto">
                          <polygon points="0 0, 6 2.5, 0 5" fill="#6366f1" fillOpacity={0.6} />
                        </marker>
                      </defs>

                      {nodes.flatMap(node =>
                        node.deps.map((depId, idx) => {
                          const from = positions.get(depId);
                          const to = positions.get(node.id);
                          if (!from || !to) return null;
                          const dx = to.x - from.x;
                          const dy = to.y - from.y;
                          const dist = Math.sqrt(dx * dx + dy * dy);
                          if (dist === 0) return null;
                          const startX = from.x + (dx / dist) * nodeRadius;
                          const startY = from.y + (dy / dist) * nodeRadius;
                          const endX = to.x - (dx / dist) * (nodeRadius + 4);
                          const endY = to.y - (dy / dist) * (nodeRadius + 4);
                          return (
                            <line
                              key={`edge-${node.id}-${depId}-${idx}`}
                              x1={startX} y1={startY} x2={endX} y2={endY}
                              stroke="#6366f1" strokeWidth={1} strokeOpacity={0.35}
                              markerEnd="url(#dag-arrow)"
                            />
                          );
                        })
                      )}

                      {nodes.map(node => {
                        const pos = positions.get(node.id);
                        if (!pos) return null;
                        const fillColor = node.status === 'completed' ? '#22c55e' :
                          node.status === 'in_progress' ? '#3b82f6' :
                          node.status === 'failed' ? '#ef4444' : '#6366f1';
                        return (
                          <g key={node.id} className="cursor-pointer"
                             onClick={() => { setSelectedRequirementId(node.id); setShowDagModal(false); }}>
                            <circle cx={pos.x} cy={pos.y} r={nodeRadius} fill={fillColor}
                                    stroke="rgba(255,255,255,0.2)" strokeWidth={1}
                                    className="transition-all hover:stroke-white hover:stroke-2" />
                            <text x={pos.x} y={pos.y} textAnchor="middle" dominantBaseline="central"
                                  fill="#fff" fontSize={8} fontWeight="bold"
                                  className="pointer-events-none select-none">{node.step}</text>
                            <title>#{node.step}: {node.desc}</title>
                          </g>
                        );
                      })}
                    </svg>
                  </div>
                );
              })()
            )}
          </div>

          {/* Bottom legend */}
          <div className="flex items-center justify-center gap-6 px-4 py-2 bg-card/50 border-t border-border text-xs text-muted-foreground">
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded-full bg-[#6366f1]" />
              <span>Pending</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded-full bg-[#8b5cf6]" />
              <span>Ready</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded-full bg-[#3b82f6]" />
              <span>In Progress</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded-full bg-[#22c55e]" />
              <span>Completed</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded-full bg-[#ef4444]" />
              <span>Failed</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-6 h-0.5 bg-[#f97316]" />
              <span>Resource Dependency</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
